import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  id: null,
  title: '',
  slug: '',
  category: 'noticias',
  campeonato_id: '',
  summary: '',
  content: '',
  cover_image_url: '',
  is_featured: false,
  status: 'draft',
  published_at: '',
  poll_options: '',
};

const CATEGORIES = [
  ['noticias', 'Noticias'],
  ['bastidores', 'Bastidores'],
  ['entrevistas', 'Entrevistas'],
  ['voz_torcida', 'Voz da torcida'],
  ['enquetes', 'Enquetes'],
  ['craque_rodada', 'Craque da rodada'],
  ['resumo_rodada', 'Resumo da rodada'],
];

const STATUS_OPTIONS = [
  ['all', 'Todos'],
  ['draft', 'Rascunhos'],
  ['published', 'Publicados'],
  ['archived', 'Arquivados'],
];

function labelForCategory(value) {
  return CATEGORIES.find(([key]) => key === value)?.[1] || 'Noticias';
}

function optionsToText(options = []) {
  return options.map((option) => option.label).filter(Boolean).join('\n');
}

function normalizeText(value) {
  return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function toDateTimeInput(value) {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
}

function formatDate(value) {
  if (!value) return 'Sem data';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function wordCount(value) {
  return String(value || '').trim().split(/\s+/).filter(Boolean).length;
}

function readingMinutes(value) {
  return Math.max(1, Math.ceil(Math.max(1, wordCount(value)) / 180));
}

function statusLabel(value) {
  switch (value) {
    case 'published':
      return 'Publicado';
    case 'archived':
      return 'Arquivado';
    default:
      return 'Rascunho';
  }
}

function editorialChecklist(form) {
  const pollOptions = form.poll_options.split('\n').map((item) => item.trim()).filter(Boolean);
  return [
    ['Titulo forte', form.title.trim().length >= 8],
    ['Resumo curto', form.summary.trim().length >= 20],
    ['Imagem de capa', Boolean(form.cover_image_url)],
    ['Texto com corpo', wordCount(form.content) >= 80],
    ['Categoria definida', Boolean(form.category)],
    ['Slug de link', Boolean(form.slug || form.title)],
    ['Enquete pronta', form.category !== 'enquetes' || pollOptions.length >= 2],
  ];
}

function buildSlug(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 180);
}

export function JournalPage({
  loading,
  error,
  data,
  championshipsData,
  onCreatePost,
  onUpdatePost,
  onDeletePost,
  onUploadCover,
  saving,
  deleting,
  uploading,
  canManage,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({ status: 'all', category: 'all', campeonato_id: 'all', search: '' });
  const [coverFile, setCoverFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const posts = data?.items || [];
  const summary = data?.summary || {};
  const championships = championshipsData?.items || [];
  const checklist = editorialChecklist(form);
  const score = Math.round((checklist.filter(([, done]) => done).length / checklist.length) * 100);
  const filteredPosts = useMemo(() => {
    const search = normalizeText(filters.search);
    return posts.filter((post) => {
      const statusMatch = filters.status === 'all' || post.status === filters.status;
      const categoryMatch = filters.category === 'all' || post.category === filters.category;
      const championshipMatch = filters.campeonato_id === 'all'
        || String(post.campeonato_id || '') === String(filters.campeonato_id);
      const textMatch = !search || normalizeText([
        post.title,
        post.summary,
        post.content,
        post.author_name,
        post.campeonato_name,
        post.slug,
      ].join(' ')).includes(search);
      return statusMatch && categoryMatch && championshipMatch && textMatch;
    });
  }, [filters, posts]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setCoverFile(null);
    setUploadMessage('');
  };

  const editPost = (row) => {
    setForm({
      id: row.id,
      title: row.title || '',
      slug: row.slug || '',
      category: row.category || 'noticias',
      campeonato_id: row.campeonato_id || '',
      summary: row.summary || '',
      content: row.content || '',
      cover_image_url: row.cover_image_url || '',
      is_featured: Boolean(row.is_featured),
      status: row.status || 'draft',
      published_at: toDateTimeInput(row.published_at),
      poll_options: optionsToText(row.poll_options || []),
    });
    setCoverFile(null);
    setUploadMessage('');
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      title: form.title,
      slug: form.slug || buildSlug(form.title),
      category: form.category,
      campeonato_id: form.campeonato_id ? Number(form.campeonato_id) : null,
      summary: form.summary,
      content: form.content,
      cover_image_url: form.cover_image_url || null,
      is_featured: Boolean(form.is_featured),
      status: form.status,
      published_at: form.published_at || null,
      poll_options: form.poll_options
        .split('\n')
        .map((label) => label.trim())
        .filter(Boolean),
    };

    if (form.id) {
      await onUpdatePost(form.id, payload);
    } else {
      await onCreatePost(payload);
    }
    resetForm();
  };

  const uploadCover = async () => {
    if (!coverFile || !onUploadCover) return;
    setUploadMessage('');
    const url = await onUploadCover(coverFile, form.id);
    setForm((current) => ({ ...current, cover_image_url: url }));
    setCoverFile(null);
    setUploadMessage('Capa enviada e aplicada na materia.');
  };

  if (loading) return <LoadingBlock label="Carregando Jornal Perifa..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns journal-workspace">
      <SectionCard
        title="Jornal Perifa"
        subtitle="Mesa editorial para noticias, bastidores, entrevistas, enquetes e cobertura dos campeonatos."
        actions={canManage ? <button className="ghost-button" type="button" onClick={resetForm}>Nova materia</button> : null}
      >
        <div className="journal-metrics">
          <div><strong>{summary.total_posts || 0}</strong><span>Total</span></div>
          <div><strong>{summary.published_posts || 0}</strong><span>Publicadas</span></div>
          <div><strong>{summary.draft_posts || 0}</strong><span>Rascunhos</span></div>
          <div><strong>{summary.scheduled_posts || 0}</strong><span>Agendadas</span></div>
          <div><strong>{summary.featured_posts || 0}</strong><span>Destaques</span></div>
          <div><strong>{summary.poll_votes || 0}</strong><span>Votos</span></div>
        </div>

        <div className="toolbar-row journal-filters">
          <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
            <option value="all">Todas categorias</option>
            {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={filters.campeonato_id} onChange={(event) => setFilters({ ...filters, campeonato_id: event.target.value })}>
            <option value="all">Todos campeonatos</option>
            <option value="">Geral Perifa</option>
            {championships.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <input
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Buscar titulo, autor, slug..."
          />
        </div>

        <DataTable
          rows={filteredPosts}
          emptyText="Nenhuma materia encontrada para os filtros."
          columns={[
            {
              key: 'cover_image_url',
              label: 'Capa',
              render: (row) => row.cover_image_url
                ? <img className="journal-thumb" src={row.cover_image_url} alt="" />
                : <span className="journal-thumb is-empty">Sem capa</span>,
            },
            {
              key: 'title',
              label: 'Materia',
              render: (row) => (
                <div className="table-title-stack">
                  <strong>{row.title}</strong>
                  <span>{row.summary || row.slug || 'Sem resumo'}</span>
                  <small>{row.author_name || 'Editorial Perifa'} • {labelForCategory(row.category)}</small>
                </div>
              ),
            },
            { key: 'campeonato_name', label: 'Contexto', render: (row) => row.campeonato_name || 'Geral Perifa' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'editorial_score',
              label: 'CEO',
              render: (row) => (
                <div className="table-title-stack">
                  <strong>{row.editorial_score || 0}%</strong>
                  <span>{row.reading_minutes || 1} min • {row.poll_total_votes || 0} votos</span>
                </div>
              ),
            },
            { key: 'published_at', label: 'Data', render: (row) => formatDate(row.published_at || row.created_at) },
            {
              key: 'actions',
              label: 'Acao',
              render: (row) => canManage ? (
                <div className="inline-actions">
                  <button className="inline-button" type="button" onClick={() => editPost(row)}>Editar</button>
                  <button className="inline-button danger-text" type="button" onClick={() => onDeletePost(row.id)} disabled={deleting}>Arquivar</button>
                </div>
              ) : 'Somente leitura',
            },
          ]}
        />
      </SectionCard>

      <SectionCard title={form.id ? 'Editar materia' : 'Nova materia'} subtitle="Publique com capa, link, agenda e checklist editorial.">
        {canManage ? (
          <form className="admin-form journal-editor-form" onSubmit={submit}>
            <div className="journal-cover-uploader">
              <div className="journal-cover-preview">
                {form.cover_image_url ? <img src={form.cover_image_url} alt="" /> : <span>Capa da materia</span>}
              </div>
              <div className="media-uploader__controls">
                <label>
                  Upload de capa
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setCoverFile(event.target.files?.[0] || null)} />
                </label>
                <button className="inline-button" type="button" onClick={uploadCover} disabled={!coverFile || uploading}>
                  {uploading ? 'Enviando...' : 'Enviar capa'}
                </button>
                {uploadMessage ? <small className="success-note compact">{uploadMessage}</small> : <small>Use JPG, PNG ou WebP. Sem custo extra.</small>}
              </div>
            </div>

            <label>
              Titulo
              <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value, slug: form.slug || buildSlug(event.target.value) })} required />
            </label>
            <label>
              Slug / link
              <input value={form.slug} onChange={(event) => setForm({ ...form, slug: buildSlug(event.target.value) })} placeholder="materia-da-final" />
            </label>
            <div className="form-row">
              <label>
                Categoria
                <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>
                Campeonato
                <select value={form.campeonato_id} onChange={(event) => setForm({ ...form, campeonato_id: event.target.value })}>
                  <option value="">Geral Perifa</option>
                  {championships.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
            </div>
            <label>
              Resumo
              <input value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} maxLength="255" />
            </label>
            <label>
              Conteudo
              <textarea rows="10" value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} required />
            </label>
            <label>
              URL de capa
              <input value={form.cover_image_url} onChange={(event) => setForm({ ...form, cover_image_url: event.target.value })} placeholder="https://..." />
            </label>
            <label>
              Opcoes de enquete
              <textarea rows="4" value={form.poll_options} onChange={(event) => setForm({ ...form, poll_options: event.target.value })} placeholder="Uma opcao por linha" />
            </label>
            <div className="form-row">
              <label>
                Status
                <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                  <option value="archived">Arquivado</option>
                </select>
              </label>
              <label>
                Publicar em
                <input type="datetime-local" value={form.published_at} onChange={(event) => setForm({ ...form, published_at: event.target.value })} />
              </label>
            </div>
            <label className="checkbox-field">
              <span>Destaque no app</span>
              <input type="checkbox" checked={form.is_featured} onChange={(event) => setForm({ ...form, is_featured: event.target.checked })} />
            </label>

            <div className="journal-checklist">
              <div>
                <span className="eyebrow">Checklist editorial</span>
                <strong>{score}% pronto para publicar</strong>
              </div>
              {checklist.map(([label, done]) => (
                <span key={label} className={done ? 'is-done' : ''}>{done ? 'OK' : '-'} {label}</span>
              ))}
            </div>

            <div className="journal-preview-card">
              <span className="eyebrow">Previa editorial</span>
              {form.cover_image_url ? <img src={form.cover_image_url} alt="" /> : null}
              <strong>{form.title || 'Titulo da materia'}</strong>
              <small>{labelForCategory(form.category)} • {statusLabel(form.status)} • Tempo de leitura: {readingMinutes(form.content)} min</small>
              <p>{form.summary || 'Resumo curto da chamada aparece aqui antes de ir para o app.'}</p>
            </div>

            <div className="inline-actions">
              <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Salvando...' : form.id ? 'Salvar materia' : 'Criar materia'}</button>
              {form.id ? <button className="inline-button" type="button" onClick={resetForm}>Cancelar edicao</button> : null}
            </div>
          </form>
        ) : <div className="empty-state">Seu perfil pode consultar o Jornal Perifa, mas nao pode publicar materias.</div>}
      </SectionCard>
    </div>
  );
}
